import * as maplibregl from "maplibre-gl";
import { STATIONS, DEFAULT_STATION, stationById } from "./stations";
import {
  getBaseUrl,
  getMode,
  setMode,
  getCustomUrl,
  setCustomUrl,
  manifestUrl,
  frameUrl,
  isDevHost,
  isLabEnabled,
  getCompositeRenderMode,
  setCompositeRenderMode,
  type BaseUrlMode,
  type CompositeRenderMode,
} from "./config";
import { ManifestPoller } from "./manifestPoller";
import { fetchAndDecodeFrame } from "./renderFrame";
import { FrameCache, frameCacheKey, type DecodedFrame } from "./frameCache";
import { cornersToMapLibreCoordinates, type ImageCorners, type LonLat } from "./geo";
import { ageState, formatMMSS, formatLatencySec } from "./ageDisplay";
import { deriveStationStrip, deriveMosaicStationStrip, pickFreshest, type StationStripEntry } from "./stationStrip";
import { frameAtOrBefore, globalTimeRange } from "./scrub";
import { FusionController, CROSSFADE_MS, BLEND_MODES, type BlendMode } from "./fusionController";
import { MosaicController } from "./mosaicController";
import {
  MosaicGlController,
  SAMPLING_MODES,
  FLOOR_ORDERS,
  FLOOR_HARDNESS_MODES,
  PALETTE_MODES,
  SIGMA_LEVELS,
  LAB_PRESET_CRISP,
  LAB_PRESET_SMOOTH,
  type SamplingMode,
  type FloorOrder,
  type FloorHardness,
  type PaletteMode,
  type LabPreset,
} from "./mosaicGlController";
import type { HotManifest, ManifestFrame } from "./types";
import { perfSummaryLine } from "./perfStages";

/** Which layer actually paints the mosaic (wide, multi-station) view when
 * `compositeRenderMode === "mosaic"` — a lab-panel-only sub-choice
 * (isLabEnabled(), config.ts), deliberately session-local (never persisted
 * to localStorage, unlike compositeRenderMode itself): GL mode is opt-in
 * research, and a lab visitor must never have it silently become their
 * default on a later, ordinary visit — same reasoning as isLabEnabled()'s
 * own doc comment, just enforced by never writing this anywhere durable at
 * all rather than by a clamp-on-read self-heal. */
type MosaicSubMode = "mosaic" | "mosaic-gl";

// Perf instrumentation start (fusionController.ts's "cold-load" measure pairs
// with this) — marked here, at module load, so it captures true app-init
// time rather than whenever RadarApp happens to construct the map.
performance.mark("radar:app-init");

const IMAGE_SOURCE_ID = "radar-frame";
const IMAGE_LAYER_ID = "radar-frame-layer";
const LOOP_FPS = 6;
const LOOP_INTERVAL_MS = 1000 / LOOP_FPS;

/** Composite pollers are staggered across this window so 5 stations don't all
 * hit the network in the same tick (workers/CONTRACTS.md hot manifest, max-age 5s). */
const COMPOSITE_POLL_INTERVAL_MS = 10_000;
/** Ticks to sweep the full scrub range during composite Play — ~10s per full loop at LOOP_FPS. */
const COMPOSITE_LOOP_STEPS = 60;
/** How long the composite scrubber must sit idle before wireCompositeTransport()
 * fires the exact-target decode — see that method's own doc comment. */
const SCRUB_SETTLE_IDLE_MS = 150;
/** Per-station composite image opacity — used only by the LEGACY painter's-
 * order layers below (compositeSourceId/compositeLayerId/createCompositeLayers),
 * a fallback for when the dev-only fusion research mode (compositeRenderMode
 * === "fusion") can't get a WebGL context (fusionController.ts's
 * `fusionAvailable`). Not the default wide view at all anymore — that's the
 * server-baked CONUS mosaic (mosaicController.ts, workers/CONTRACTS.md
 * "Mosaic-head"), which needs no such fallback since it renders through the
 * same plain image-layer path single-station drill-down always has. */
const COMPOSITE_OPACITY = 0.85;
/** 1x1 transparent PNG: placeholder image for a station's composite layer before its
 * first frame decodes, and shown again when scrubbed earlier than a station's coverage. */
const TRANSPARENT_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=";

type DisplayMode = "live" | "loop";
type ViewMode = "composite" | "station";

function darkBasemapStyle(): maplibregl.StyleSpecification {
  return {
    version: 8,
    sources: {
      basemap: {
        type: "raster",
        tiles: [
          "https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
          "https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
          "https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
          "https://d.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}@2x.png",
        ],
        tileSize: 256,
        attribution: "© OpenStreetMap contributors © CARTO",
      },
    },
    layers: [{ id: "basemap", type: "raster", source: "basemap" }],
  };
}

function compositeSourceId(stationId: string): string {
  return `radar-frame-composite-src-${stationId}`;
}

function compositeLayerId(stationId: string): string {
  return `radar-frame-composite-${stationId}`;
}

/** Small quad around a station's point, used only until its first real frame decodes. */
function placeholderCoordinates(lon: number, lat: number): [LonLat, LonLat, LonLat, LonLat] {
  const d = 0.05;
  return [
    [lon - d, lat + d],
    [lon + d, lat + d],
    [lon + d, lat - d],
    [lon - d, lat - d],
  ];
}

function stationsCentroid(): LonLat {
  const lon = STATIONS.reduce((sum, s) => sum + s.lon, 0) / STATIONS.length;
  const lat = STATIONS.reduce((sum, s) => sum + s.lat, 0) / STATIONS.length;
  return [lon, lat];
}

export class RadarApp {
  private map: maplibregl.Map;
  private mapReady = false;
  private cache = new FrameCache(80);

  private viewMode: ViewMode;

  // ---- composite render engine (which one drives the default wide view) ----
  private compositeRenderMode: CompositeRenderMode;

  // ---- mosaic (default composite-view layer — server-baked CONUS mosaic) ----
  private mosaic!: MosaicController;

  // ---- mosaic GL (lab-panel-only smoothing-experiments sub-mode of "mosaic") ----
  private mosaicGl!: MosaicGlController;
  private mosaicSubMode: MosaicSubMode = "mosaic";

  // ---- fusion (dev-only composite research mode — client-side per-station blend) ----
  private fusion!: FusionController;

  // ---- single-station mode state (unchanged from the pre-composite client) ----
  private poller: ManifestPoller;
  private station: string;
  private manifest: HotManifest | null = null;
  private mode: DisplayMode = "live";
  private loopPlaying = false;
  private loopIndex = 0;
  private loopTimer: ReturnType<typeof setInterval> | undefined;
  /** ts of the frame currently shown, and its manifest metadata (for the age counter). */
  private displayedTs: string | null = null;
  private displayedMeta: ManifestFrame | null = null;
  /** Monotonic token so a stale in-flight frame fetch can't clobber a newer one. */
  private loadToken = 0;

  // ---- composite mode state ----
  private compositePollers = new Map<string, ManifestPoller>();
  private compositeStaggerTimers: ReturnType<typeof setTimeout>[] = [];
  private compositeManifests = new Map<string, HotManifest | null>();
  /** ts currently painted per station (null = showing the transparent placeholder). */
  private compositeDisplayedTs = new Map<string, string | null>();
  /** Geographic extent of each station's currently-painted frame (null = placeholder,
   * no real coverage yet). Used for manual click/hover hit-testing — see the comment
   * on wireCompositeMapInteractions() for why this can't use MapLibre's layer events. */
  private compositeFrameBounds = new Map<string, ImageCorners | null>();
  /** Per-station monotonic token, same purpose as loadToken but keyed by station. */
  private compositeLoadTokens = new Map<string, number>();
  private compositeMode: DisplayMode = "live";
  private compositeLoopPlaying = false;
  private compositeScrubTimeMs = 0;
  private compositeLoopTimer: ReturnType<typeof setInterval> | undefined;
  private stripItemEls = new Map<string, { dot: HTMLElement; age: HTMLElement }>();
  private hasFitInitialBounds = false;

  private el = {
    map: req("map"),
    // composite
    compositeHud: req("composite-hud"),
    cAgePanel: req("c-age-panel"),
    cAgeValue: req("c-age-value"),
    cAgeLabel: req("c-age-label"),
    cMetaStation: req("c-meta-station"),
    cMetaPipeline: req("c-meta-pipeline"),
    stationStrip: req("station-strip"),
    cStatusLine: req("c-status-line"),
    cLiveBtn: req<HTMLButtonElement>("c-live-btn"),
    cPlayBtn: req<HTMLButtonElement>("c-play-btn"),
    cScrubber: req<HTMLInputElement>("c-scrubber"),
    cFrameTs: req("c-frame-ts"),
    // single-station
    stationHud: req("station-hud"),
    backBtn: req<HTMLButtonElement>("back-btn"),
    agePanel: req("age-panel"),
    ageValue: req("age-value"),
    metaStation: req("meta-station"),
    metaVcp: req("meta-vcp"),
    metaBake: req("meta-bake"),
    stationSelect: req("station-select"),
    statusLine: req("status-line"),
    liveBtn: req<HTMLButtonElement>("live-btn"),
    playBtn: req<HTMLButtonElement>("play-btn"),
    scrubber: req<HTMLInputElement>("scrubber"),
    frameTs: req("frame-ts"),
    // shared
    modePicker: req<HTMLSelectElement>("mode-picker"),
    customUrl: req<HTMLInputElement>("custom-url"),
    renderModePicker: req<HTMLSelectElement>("render-mode-picker"),
    blendModePicker: req<HTMLSelectElement>("blend-mode-picker"),
    cFloorCheckbox: req<HTMLInputElement>("c-floor-checkbox"),
    // lab panel (WebGL smoothing experiments, isLabEnabled() only)
    labPanel: req("lab-panel"),
    labRenderSelect: req<HTMLSelectElement>("lab-render-select"),
    labSamplingSelect: req<HTMLSelectElement>("lab-sampling-select"),
    labDespeckleCheckbox: req<HTMLInputElement>("lab-despeckle-checkbox"),
    labSigmaSelect: req<HTMLSelectElement>("lab-sigma-select"),
    labFloorOrderSelect: req<HTMLSelectElement>("lab-floor-order-select"),
    labFloorHardnessSelect: req<HTMLSelectElement>("lab-floor-hardness-select"),
    labPaletteSelect: req<HTMLSelectElement>("lab-palette-select"),
    labFloorCheckbox: req<HTMLInputElement>("lab-floor-checkbox"),
    labPresetCrisp: req<HTMLButtonElement>("lab-preset-crisp"),
    labPresetSmooth: req<HTMLButtonElement>("lab-preset-smooth"),
    labStatus: req("lab-status"),
  };

  constructor() {
    const requestedStation = new URLSearchParams(location.search).get("station");
    const validRequestedStation = requestedStation && STATIONS.some((s) => s.id === requestedStation) ? requestedStation : null;
    this.viewMode = validRequestedStation ? "station" : "composite";
    this.station = validRequestedStation ?? DEFAULT_STATION;
    this.compositeRenderMode = getCompositeRenderMode();

    const initialCenter: LonLat =
      this.viewMode === "station" ? [stationById(this.station).lon, stationById(this.station).lat] : stationsCentroid();
    this.map = new maplibregl.Map({
      container: this.el.map,
      style: darkBasemapStyle(),
      center: initialCenter,
      zoom: this.viewMode === "station" ? 6.2 : 5,
      attributionControl: { compact: true },
    });
    this.mosaic = new MosaicController(this.map, () => getBaseUrl(), () => {
      this.refreshCompositeScrubRange();
      this.renderStationStrip();
    });
    this.mosaicGl = new MosaicGlController(
      this.map,
      () => getBaseUrl(),
      () => this.updateLabStatus(),
      () => this.onMosaicGlFallback(),
    );
    this.fusion = new FusionController(this.map, () => getBaseUrl(), () => this.renderStationStrip());

    this.map.on("load", () => {
      this.mapReady = true;
      // Added first so the legacy per-station fallback layers (below) would
      // paint on top of it during the brief crossfade if ever both were
      // visible at once (they never are in steady state — see
      // fusionAvailable branches below — but keeps draw order sane if that
      // ever changes).
      this.fusion.init();
      this.mosaic.init();
      this.mosaicGl.init();
      this.createCompositeLayers();
      if (this.viewMode === "composite") {
        this.fitAllStationBounds();
        // activateCompositeRenderMode() belongs here, not the constructor:
        // fusion.init()/mosaic.init() (just above) are what stand up the
        // WebGL compositor / image layers, and both only run once "load"
        // fires — calling setActive(true) any earlier finds fusionAvailable
        // still false (its pre-init default) and silently no-ops,
        // permanently missing the real activation (verified: this was a
        // real bug, not a hypothetical — fusion never rendered a single
        // frame on cold load until this was fixed).
        this.activateCompositeRenderMode();
      } else {
        this.setCompositeLayersVisibility(false);
      }
      this.hasFitInitialBounds = true;
    });

    this.poller = new ManifestPoller(
      () => manifestUrl(getBaseUrl(), this.station),
      (m) => this.onManifest(m),
      (err) => this.setStatus(`manifest: ${(err as Error).message ?? err}`, true),
      10_000,
    );

    this.buildStationSelector();
    this.buildSourceSelector();
    this.buildRenderModeSelector();
    this.buildBlendModeSelector();
    this.buildFloorToggle();
    this.buildLabPanel();
    this.buildStationStrip();
    this.wireTransport();
    this.wireCompositeTransport();
    this.wireCompositeMapInteractions();
    this.wireBackButton();
    setInterval(() => this.tick(), 1000);

    this.el.compositeHud.hidden = this.viewMode !== "composite";
    this.el.stationHud.hidden = this.viewMode !== "station";
    this.syncStationTabsUi();

    if (this.viewMode === "composite") {
      // Manifest polling can start immediately (doesn't need the map loaded);
      // the actual render activation happens in the "load" handler above
      // instead, once fusion.init()/mosaic.init() exist to activate.
      if (this.compositeRenderMode === "mosaic") {
        this.mosaic.start();
        this.mosaicGl.start();
      } else {
        this.startCompositePollers();
        this.fusion.start();
      }
    } else {
      this.poller.start();
    }

    // dev convenience: `window.__radar` for console debugging.
    (window as unknown as { __radar: unknown }).__radar = { map: this.map, app: this };
  }

  // ---- view mode transitions ----

  private enterCompositeMode(): void {
    if (this.viewMode === "composite") return;
    this.viewMode = "composite";
    this.poller.stop();
    this.mode = "live";
    this.stopLoop();
    this.updateTransportUi();
    this.el.stationHud.hidden = true;
    this.el.compositeHud.hidden = false;
    this.setStationLayerVisibility(false);
    this.syncUrl();
    this.activateCompositeRenderMode();
    // Only auto-fit once, on first load — re-entering composite via the back
    // button shouldn't yank the user's pan/zoom back to the Gulf-wide view.
    if (!this.hasFitInitialBounds) {
      this.fitAllStationBounds();
      this.hasFitInitialBounds = true;
    }
  }

  private enterStationMode(id: string): void {
    const wasComposite = this.viewMode === "composite";
    this.viewMode = "station";
    if (wasComposite) {
      this.stopCompositePollers();
      this.stopCompositeLoop();
      this.fusion.stop();
      this.fusion.setActive(false);
      this.mosaic.stop();
      this.mosaic.setActive(false);
      this.mosaicGl.stop();
      this.mosaicGl.setActive(false);
    }
    this.el.compositeHud.hidden = true;
    this.el.stationHud.hidden = false;
    this.setCompositeLayersVisibility(false);
    this.setStationLayerVisibility(true);
    this.selectStation(id, true);
  }

  /** Starts/activates whichever composite render engine `compositeRenderMode`
   * currently selects, and stops/deactivates the other — called on every
   * composite-view activation (cold load, back-button re-entry) and again on
   * a dev's render-mode picker change while already in composite view.
   * Mosaic needs no WebGL fallback of its own (it's a single decoded raster
   * image layer — the same rendering path single-station drill-down already
   * uses, just wired to a different manifest/frame source); fusion's own
   * fusionAvailable/legacy-layer fallback is untouched, doing exactly what
   * it did before the mosaic existed. The legacy per-station layers stay
   * layout-`visible` for the whole composite view (unchanged — opacity, not
   * visibility, is what already toggled them on/off before this change, so
   * their crossfade keeps working if a dev flips fusionAvailable's fallback
   * on); this method's job is only to make sure their OPACITY is forced to 0
   * in mosaic mode, since nothing else would reset it if a dev had
   * previously been in the fusion-fallback path this session. */
  private activateCompositeRenderMode(): void {
    this.setCompositeLayersVisibility(true);
    if (this.compositeRenderMode === "mosaic") {
      this.stopCompositePollers();
      this.fusion.stop();
      this.fusion.setActive(false);
      this.setCompositeLayersOpacity(0);
      this.mosaic.start();
      this.mosaicGl.start();
      this.activateMosaicSubMode();
    } else {
      this.mosaic.stop();
      this.mosaic.setActive(false);
      this.mosaicGl.stop();
      this.mosaicGl.setActive(false);
      this.startCompositePollers();
      this.fusion.start();
      this.fusion.setActive(true);
      if (!this.fusion.fusionAvailable) {
        this.setCompositeLayersOpacity(COMPOSITE_OPACITY);
        // Catches any manifest that arrived (and was correctly ignored by
        // onCompositeManifest's own initialized-gated guard) before this
        // point — without this, a station whose first poll won that race
        // wouldn't get its legacy-path frame fetched until the next poll tick.
        this.refreshAllCompositeFrames();
      } else {
        this.setCompositeLayersOpacity(0);
      }
    }
    this.el.blendModePicker.hidden = !import.meta.env.DEV || this.compositeRenderMode !== "fusion";
    this.updateCompositeTransportUi();
    this.refreshCompositeScrubRange();
    this.renderStationStrip();
  }

  /** Which LAYER actually paints when compositeRenderMode is "mosaic" — the
   * CPU path (mosaicController.ts, default) or the lab-panel-only "Mosaic
   * GL" smoothing-experiments path (mosaicGlController.ts). Both
   * controllers keep POLLING regardless of `mosaicSubMode` (started
   * together in activateCompositeRenderMode()'s "mosaic" branch) — this
   * method only toggles which one is VISIBLE, so flipping the lab panel's
   * render-mode select never re-fetches a frame that's already in flight
   * for the other engine, and station-strip/hero-panel data (always sourced
   * from `this.mosaic`, unaffected by this method) never has a gap. Falls
   * back to the CPU layer if GL isn't actually available yet/anymore
   * (mosaicGl.glAvailable — construction failure or a lost context). */
  private activateMosaicSubMode(): void {
    if (this.mosaicSubMode === "mosaic-gl" && this.mosaicGl.glAvailable) {
      this.mosaic.setActive(false);
      this.mosaicGl.setActive(true);
    } else {
      this.mosaicGl.setActive(false);
      this.mosaic.setActive(true);
    }
    this.updateLabStatus();
  }

  /** mosaicGlController.ts's onFallback callback: construction failed, or a
   * live WebGL context was lost mid-session. Snaps the lab panel's render
   * select back to "mosaic" and reactivates the CPU layer if the GL layer
   * was the one currently on screen — the "WebGL unavailable/context-lost
   * -> CPU mosaic path" fallback requirement. */
  private onMosaicGlFallback(): void {
    this.mosaicSubMode = "mosaic";
    this.el.labRenderSelect.value = "mosaic";
    if (this.viewMode === "composite" && this.compositeRenderMode === "mosaic" && this.mapReady) {
      this.mosaic.setActive(true);
    }
    this.updateLabStatus();
  }

  /** Lab panel status line + the render select's availability — re-derived
   * whenever mosaicGl's own data changes (a new frame decoded) or the sub-
   * mode/fallback state changes, so the panel never shows a stale ts/IFD.
   * Also carries perfStages.ts's rolling per-stage timing summary (the
   * "MEASURE FIRST" scrub-latency instrumentation) — dev/lab-only display,
   * same gating as the rest of this panel. */
  private updateLabStatus(): void {
    if (this.el.labPanel.hidden) return;
    const engine = this.mosaicSubMode === "mosaic-gl" ? this.mosaicGl.statusLine() : "CPU mosaic path (mosaicController.ts)";
    this.el.labStatus.textContent = `${engine}\n${perfSummaryLine()}`;
  }

  // ---- station selector (single-station mode) ----

  private buildStationSelector(): void {
    this.el.stationSelect.innerHTML = "";
    for (const s of STATIONS) {
      const btn = document.createElement("button");
      btn.className = "station-btn";
      btn.textContent = s.id;
      btn.setAttribute("role", "tab");
      btn.setAttribute("aria-selected", String(s.id === this.station));
      btn.addEventListener("click", () => this.selectStation(s.id));
      this.el.stationSelect.appendChild(btn);
    }
  }

  private syncStationTabsUi(): void {
    for (const child of Array.from(this.el.stationSelect.children)) {
      child.setAttribute("aria-selected", String((child as HTMLElement).textContent === this.station));
    }
  }

  /** `force` lets enterStationMode() re-arm the poller/UI even when `id` is already
   * `this.station` (e.g. returning to a station you were already viewing). */
  private selectStation(id: string, force = false): void {
    if (id === this.station && !force) return;
    this.station = id;
    this.manifest = null;
    this.displayedTs = null;
    this.displayedMeta = null;
    this.mode = "live";
    this.stopLoop();
    this.updateTransportUi();
    this.setStatus("loading manifest…");
    this.syncStationTabsUi();
    this.syncUrl();

    const st = stationById(id);
    this.map.flyTo({ center: [st.lon, st.lat], zoom: 6.2, essential: true });
    this.poller.start(); // restarts on the new station's manifest URL
  }

  // ---- back button (station -> composite) ----

  private wireBackButton(): void {
    this.el.backBtn.addEventListener("click", () => this.enterCompositeMode());
  }

  // ---- data source selector (shared: restarts whichever poller set is active) ----

  private buildSourceSelector(): void {
    // Deployed hosts get only modes that can actually work there (config.isDevHost;
    // dev-only options are marked in index.html).
    if (!isDevHost()) {
      for (const opt of Array.from(this.el.modePicker.querySelectorAll("option[data-dev-only]"))) {
        opt.remove();
      }
    }
    const mode = getMode();
    this.el.modePicker.value = mode;
    this.el.customUrl.hidden = mode !== "custom";
    this.el.customUrl.value = getCustomUrl();

    this.el.modePicker.addEventListener("change", () => {
      const next = this.el.modePicker.value as BaseUrlMode;
      setMode(next);
      this.el.customUrl.hidden = next !== "custom";
      this.restartActivePolling();
    });

    this.el.customUrl.addEventListener("change", () => {
      setCustomUrl(this.el.customUrl.value.trim());
      if (getMode() === "custom") this.restartActivePolling();
    });
  }

  /** Composite render-engine picker: mosaic (default, works on any host) vs.
   * the dev-only per-station WebGL fusion research mode. Same dev-gating
   * pattern as buildSourceSelector above (data-dev-only options stripped on
   * non-dev hosts) plus config.ts's localStorage self-heal — unlike
   * buildBlendModeSelector's plain import.meta.env.DEV hide below, this
   * choice is PERSISTED, so it needs the same escape-hatch discipline
   * getMode() already has for the base-URL mode picker. */
  private buildRenderModeSelector(): void {
    if (!isDevHost()) {
      for (const opt of Array.from(this.el.renderModePicker.querySelectorAll("option[data-dev-only]"))) {
        opt.remove();
      }
    }
    this.el.renderModePicker.value = this.compositeRenderMode;
    this.el.renderModePicker.addEventListener("change", () => {
      const next = this.el.renderModePicker.value as CompositeRenderMode;
      if (next === this.compositeRenderMode) return;
      setCompositeRenderMode(next);
      this.compositeRenderMode = next;
      if (this.viewMode === "composite" && this.mapReady) this.activateCompositeRenderMode();
    });
  }

  /** Runtime blend-mode switch (workers/CONTRACTS.md: "blend rule a runtime
   * parameter" — nearest-station-wins/max/owner-with-fallback is an active
   * research knob, not something worth re-baking client code to try). Dev-
   * mode only (import.meta.env.DEV) per the brief; hidden — not removed —
   * in production builds so it still exists for a local `npm run build &&
   * npm run preview` check. */
  private buildBlendModeSelector(): void {
    this.el.blendModePicker.hidden = !import.meta.env.DEV;
    this.el.blendModePicker.innerHTML = "";
    for (const mode of BLEND_MODES) {
      const opt = document.createElement("option");
      opt.value = mode;
      opt.textContent = mode;
      this.el.blendModePicker.appendChild(opt);
    }
    this.el.blendModePicker.value = this.fusion.getBlendMode();
    this.el.blendModePicker.addEventListener("change", () => {
      this.fusion.setBlendMode(this.el.blendModePicker.value as BlendMode);
    });
  }

  /** Display floor toggle ("precip only" default vs. "all returns") — a
   * real, visible HUD control (unlike buildBlendModeSelector's dev-only
   * picker above): the floor changes what the composite view shows by
   * default, so it needs to be discoverable, not just a debug knob. Drives
   * BOTH composite render engines at once (not just whichever is currently
   * active) so switching compositeRenderMode mid-session never desyncs the
   * checkbox from what a newly-activated engine would show. Lives inside
   * #composite-hud in index.html, so it's naturally hidden in station
   * drill-down, which always shows everything regardless of this setting. */
  private buildFloorToggle(): void {
    this.el.cFloorCheckbox.checked = this.mosaic.getFloorEnabled();
    this.el.cFloorCheckbox.addEventListener("change", () => {
      this.setFloorEnabled(this.el.cFloorCheckbox.checked);
    });
  }

  /** Fans a floor-enabled change out to all three composite render engines
   * at once (mosaic, fusion, mosaic-gl) and keeps both checkboxes — the
   * always-visible #c-floor-checkbox and the lab panel's own copy — in
   * sync with each other, regardless of which one the user actually
   * clicked. Extracted so buildFloorToggle()/buildLabPanel() share one
   * fan-out instead of two near-duplicate listeners drifting apart. */
  private setFloorEnabled(enabled: boolean): void {
    this.mosaic.setFloorEnabled(enabled);
    this.fusion.setFloorEnabled(enabled);
    this.mosaicGl.setFloorEnabled(enabled);
    this.el.cFloorCheckbox.checked = enabled;
    this.el.labFloorCheckbox.checked = enabled;
  }

  /** The WebGL smoothing-experiments panel (mosaicGlController.ts) — visible
   * only when config.ts's isLabEnabled() is true. Populates the sampling/
   * floor-order/palette pickers from mosaicGlController.ts's own toggle enum
   * arrays (same "build <option> per enum value" pattern
   * buildBlendModeSelector uses for BLEND_MODES) and wires the render-mode
   * select to `mosaicSubMode` (activateMosaicSubMode()) rather than
   * `compositeRenderMode` — this panel only ever chooses between the two
   * ways of painting the MOSAIC view, never fusion (that stays the main
   * HUD's #render-mode-picker's job). */
  private buildLabPanel(): void {
    this.el.labPanel.hidden = !isLabEnabled();

    for (const mode of SAMPLING_MODES) {
      const opt = document.createElement("option");
      opt.value = mode;
      opt.textContent = mode;
      this.el.labSamplingSelect.appendChild(opt);
    }
    this.el.labSamplingSelect.value = this.mosaicGl.getSampling();
    this.el.labSamplingSelect.addEventListener("change", () => {
      this.mosaicGl.setSampling(this.el.labSamplingSelect.value as SamplingMode);
    });

    this.el.labDespeckleCheckbox.checked = this.mosaicGl.getDespeckleEnabled();
    this.el.labDespeckleCheckbox.addEventListener("change", () => {
      this.mosaicGl.setDespeckleEnabled(this.el.labDespeckleCheckbox.checked);
    });

    for (const sigma of SIGMA_LEVELS) {
      const opt = document.createElement("option");
      opt.value = String(sigma);
      opt.textContent = sigma === 0 ? "off" : String(sigma);
      this.el.labSigmaSelect.appendChild(opt);
    }
    this.el.labSigmaSelect.value = String(this.mosaicGl.getSigma());
    this.el.labSigmaSelect.addEventListener("change", () => {
      this.mosaicGl.setSigma(Number(this.el.labSigmaSelect.value));
    });

    for (const order of FLOOR_ORDERS) {
      const opt = document.createElement("option");
      opt.value = order;
      opt.textContent = order;
      this.el.labFloorOrderSelect.appendChild(opt);
    }
    this.el.labFloorOrderSelect.value = this.mosaicGl.getFloorOrder();
    this.el.labFloorOrderSelect.addEventListener("change", () => {
      this.mosaicGl.setFloorOrder(this.el.labFloorOrderSelect.value as FloorOrder);
    });

    for (const hardness of FLOOR_HARDNESS_MODES) {
      const opt = document.createElement("option");
      opt.value = hardness;
      opt.textContent = hardness;
      this.el.labFloorHardnessSelect.appendChild(opt);
    }
    this.el.labFloorHardnessSelect.value = this.mosaicGl.getFloorHardness();
    this.el.labFloorHardnessSelect.addEventListener("change", () => {
      this.mosaicGl.setFloorHardness(this.el.labFloorHardnessSelect.value as FloorHardness);
    });

    for (const mode of PALETTE_MODES) {
      const opt = document.createElement("option");
      opt.value = mode;
      opt.textContent = mode;
      this.el.labPaletteSelect.appendChild(opt);
    }
    this.el.labPaletteSelect.value = this.mosaicGl.getPalette();
    this.el.labPaletteSelect.addEventListener("change", () => {
      this.mosaicGl.setPalette(this.el.labPaletteSelect.value as PaletteMode);
    });

    this.el.labRenderSelect.value = this.mosaicSubMode;
    this.el.labRenderSelect.addEventListener("change", () => {
      const next = this.el.labRenderSelect.value as MosaicSubMode;
      if (next === "mosaic-gl" && !this.mosaicGl.glAvailable) {
        // WebGL unavailable — snap the select back rather than silently
        // accepting a selection that can never actually render.
        this.el.labRenderSelect.value = "mosaic";
        return;
      }
      if (next === this.mosaicSubMode) return;
      this.mosaicSubMode = next;
      if (this.viewMode === "composite" && this.compositeRenderMode === "mosaic" && this.mapReady) {
        this.activateMosaicSubMode();
      }
    });

    this.el.labFloorCheckbox.checked = this.mosaicGl.getFloorEnabled();
    this.el.labFloorCheckbox.addEventListener("change", () => {
      this.setFloorEnabled(this.el.labFloorCheckbox.checked);
    });

    this.el.labPresetCrisp.addEventListener("click", () => this.applyLabPreset(LAB_PRESET_CRISP));
    this.el.labPresetSmooth.addEventListener("click", () => this.applyLabPreset(LAB_PRESET_SMOOTH));
  }

  /** One-click path to a full toggle combination (the study's "ship spec
   * target" combo, and its "crisp"/original-behavior counterpart) — sets
   * every mosaicGl toggle AND syncs every lab-panel control to match, so the
   * dropdowns never silently disagree with what's actually rendering.
   * Deliberately NOT an auto-linking behavior between individual toggles
   * (e.g. sigma auto-selecting a floor hardness) — these buttons are the
   * one explicit mechanism for reaching a specific combination; every
   * individual control stays a plain, independent toggle otherwise. */
  private applyLabPreset(preset: LabPreset): void {
    this.mosaicGl.setSampling(preset.sampling);
    this.mosaicGl.setDespeckleEnabled(preset.despeckle);
    this.mosaicGl.setSigma(preset.sigma);
    this.mosaicGl.setFloorOrder(preset.floorOrder);
    this.mosaicGl.setFloorHardness(preset.floorHardness);
    this.mosaicGl.setPalette(preset.palette);
    this.el.labSamplingSelect.value = preset.sampling;
    this.el.labDespeckleCheckbox.checked = preset.despeckle;
    this.el.labSigmaSelect.value = String(preset.sigma);
    this.el.labFloorOrderSelect.value = preset.floorOrder;
    this.el.labFloorHardnessSelect.value = preset.floorHardness;
    this.el.labPaletteSelect.value = preset.palette;
  }

  private restartActivePolling(): void {
    if (this.viewMode === "composite") {
      if (this.compositeRenderMode === "mosaic") {
        this.mosaic.start();
        this.mosaicGl.start();
      } else {
        this.startCompositePollers();
        this.fusion.start();
      }
    } else {
      this.poller.start();
    }
  }

  // ---- single-station manifest handling (unchanged) ----

  private onManifest(manifest: HotManifest): void {
    this.manifest = manifest;
    this.setStatus("");
    this.el.metaStation.textContent = manifest.station;
    this.el.metaVcp.textContent = manifest.vcp !== null ? `VCP ${manifest.vcp}` : "VCP —";

    const frameCount = manifest.frames.length;
    this.el.scrubber.max = String(Math.max(0, frameCount - 1));

    if (this.mode === "live") {
      const latest = manifest.latest ?? manifest.frames.at(-1) ?? null;
      if (latest && latest.ts !== this.displayedTs) {
        this.loopIndex = frameCount - 1;
        this.el.scrubber.value = String(this.loopIndex);
        this.el.frameTs.textContent = "live";
        void this.showFrame(latest.ts);
      }
    }
  }

  private findManifestFrame(ts: string): ManifestFrame | null {
    if (!this.manifest) return null;
    const found = this.manifest.frames.find((f) => f.ts === ts);
    if (found) return found;
    if (this.manifest.latest && this.manifest.latest.ts === ts) {
      return { ts, dataTimeFirstMs: 0, dataTimeLastMs: this.manifest.latest.dataTimeLastMs, bakeTimeMs: this.manifest.latest.bakeTimeMs, size: 0 };
    }
    return null;
  }

  // ---- single-station frame loading / display (unchanged) ----

  private async showFrame(ts: string): Promise<void> {
    const station = this.station;
    const token = ++this.loadToken;
    const key = frameCacheKey(station, ts);

    let decoded: DecodedFrame | undefined = this.cache.get(key);
    if (!decoded) {
      try {
        decoded = await fetchAndDecodeFrame(frameUrl(getBaseUrl(), station, ts));
      } catch (err) {
        this.setStatus(`frame ${ts}: ${(err as Error).message ?? err}`, true);
        return;
      }
      if (token !== this.loadToken || station !== this.station) {
        // superseded by a newer request; still worth caching, but don't apply it.
        this.cache.set(key, decoded);
        return;
      }
      this.cache.set(key, decoded);
    }
    if (token !== this.loadToken || station !== this.station) return;

    this.applyFrameToMap(decoded);
    this.displayedTs = ts;
    this.displayedMeta = this.findManifestFrame(ts);
  }

  private applyFrameToMap(decoded: DecodedFrame): void {
    const coordinates = cornersToMapLibreCoordinates(decoded.corners);
    if (!this.mapReady) {
      this.map.once("load", () => this.applyFrameToMap(decoded));
      return;
    }
    const existing = this.map.getSource(IMAGE_SOURCE_ID) as maplibregl.ImageSource | undefined;
    if (existing) {
      existing.updateImage({ url: decoded.url, coordinates });
    } else {
      this.map.addSource(IMAGE_SOURCE_ID, { type: "image", url: decoded.url, coordinates });
      this.map.addLayer({
        id: IMAGE_LAYER_ID,
        type: "raster",
        source: IMAGE_SOURCE_ID,
        // "nearest", not the mosaic/composite layers' default "linear": station
        // drill-down is where the honest raw bins matter (goal: keep pixelated
        // truth here, smooth everywhere else). MapLibre renders raster `image`
        // sources into its own WebGL canvas, so the CSS `image-rendering`
        // property (which only governs how the browser itself upscales an
        // <img>/<canvas> element) doesn't reach this — `raster-resampling` is
        // the actual texture-filtering knob, hence a paint property here
        // rather than a style.css rule.
        paint: { "raster-opacity": 0.85, "raster-resampling": "nearest" },
      });
    }
  }

  private setStationLayerVisibility(visible: boolean): void {
    if (!this.mapReady || !this.map.getLayer(IMAGE_LAYER_ID)) return;
    this.map.setLayoutProperty(IMAGE_LAYER_ID, "visibility", visible ? "visible" : "none");
  }

  // ---- single-station hero age counter (unchanged) ----

  private tickAgeCounter(): void {
    if (!this.displayedMeta) {
      this.el.ageValue.textContent = "--:--";
      this.el.agePanel.dataset.state = "stale";
      this.el.metaBake.textContent = "bake —";
      return;
    }
    const ageSec = (Date.now() - this.displayedMeta.dataTimeLastMs) / 1000;
    this.el.ageValue.textContent = formatMMSS(ageSec);
    this.el.agePanel.dataset.state = ageState(ageSec);
    const latency = this.displayedMeta.bakeTimeMs - this.displayedMeta.dataTimeLastMs;
    this.el.metaBake.textContent = `bake ${formatLatencySec(latency)}`;
  }

  // ---- single-station loop transport (unchanged) ----

  private wireTransport(): void {
    this.el.liveBtn.addEventListener("click", () => this.goLive());
    this.el.playBtn.addEventListener("click", () => this.togglePlay());

    let rafPending = false;
    let pendingIndex = 0;
    this.el.scrubber.addEventListener("input", () => {
      this.enterLoopMode();
      this.stopLoopTimer();
      pendingIndex = Number(this.el.scrubber.value);
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(() => {
          rafPending = false;
          this.seekTo(pendingIndex);
        });
      }
    });
  }

  private enterLoopMode(): void {
    if (this.mode === "loop") return;
    this.mode = "loop";
    this.updateTransportUi();
  }

  private goLive(): void {
    this.mode = "live";
    this.loopPlaying = false;
    this.stopLoopTimer();
    this.updateTransportUi();
    if (this.manifest) {
      const latest = this.manifest.latest ?? this.manifest.frames.at(-1) ?? null;
      if (latest) {
        this.loopIndex = this.manifest.frames.length - 1;
        this.el.scrubber.value = String(this.loopIndex);
        this.el.frameTs.textContent = "live";
        void this.showFrame(latest.ts);
      }
    }
  }

  private togglePlay(): void {
    this.enterLoopMode();
    this.loopPlaying = !this.loopPlaying;
    this.updateTransportUi();
    if (this.loopPlaying) this.startLoopTimer();
    else this.stopLoopTimer();
  }

  private startLoopTimer(): void {
    this.stopLoopTimer();
    this.loopTimer = setInterval(() => {
      if (!this.manifest || this.manifest.frames.length === 0) return;
      this.loopIndex = (this.loopIndex + 1) % this.manifest.frames.length;
      this.el.scrubber.value = String(this.loopIndex);
      this.applyLoopFrame();
    }, LOOP_INTERVAL_MS);
  }

  private stopLoopTimer(): void {
    if (this.loopTimer !== undefined) clearInterval(this.loopTimer);
    this.loopTimer = undefined;
  }

  private stopLoop(): void {
    this.loopPlaying = false;
    this.stopLoopTimer();
  }

  private seekTo(index: number): void {
    this.loopIndex = index;
    this.applyLoopFrame();
  }

  private applyLoopFrame(): void {
    if (!this.manifest) return;
    const frame = this.manifest.frames[this.loopIndex];
    if (!frame) return;
    this.el.frameTs.textContent = formatFrameTs(frame.ts);
    void this.showFrame(frame.ts);
  }

  private updateTransportUi(): void {
    this.el.liveBtn.setAttribute("aria-pressed", String(this.mode === "live"));
    this.el.playBtn.setAttribute("aria-pressed", String(this.loopPlaying));
    this.el.playBtn.textContent = this.loopPlaying ? "⏸ Pause" : "▶ Play";
  }

  // ================= composite (multi-station) mode =================

  private createCompositeLayers(): void {
    for (const s of STATIONS) {
      const sourceId = compositeSourceId(s.id);
      if (this.map.getSource(sourceId)) continue;
      this.map.addSource(sourceId, { type: "image", url: TRANSPARENT_PIXEL, coordinates: placeholderCoordinates(s.lon, s.lat) });
      const layerId = compositeLayerId(s.id);
      this.map.addLayer({
        id: layerId,
        type: "raster",
        source: sourceId,
        // Starts at opacity 0, not 0.85: these are the fusion-fallback-only
        // layers (activateCompositeRenderMode() below only raises this above
        // 0 when compositeRenderMode is "fusion" AND WebGL is unavailable) —
        // invisible by default now that the mosaic is the production
        // default. A hard `visibility` toggle would flash on/off, so this
        // fades in/out with the same crossfade as ordinary frame swaps.
        paint: { "raster-opacity": 0 },
      });
      this.map.setPaintProperty(layerId, "raster-opacity-transition", { duration: CROSSFADE_MS });
    }
    // Layers are added in STATIONS (alphabetical) order, so draw order — and
    // therefore which station "wins" on overlap — is deterministic by id.
  }

  /** MapLibre's per-layer `map.on('click'|'mouseenter', layerId, cb)` delegation is
   * implemented via queryRenderedFeatures, which does not support raster layers
   * (verified empirically: it returns zero features for an `image`-source raster
   * layer even dead-center on it) — so footprint clicks need a manual hit test
   * instead of the layer-scoped listener pattern used elsewhere in MapLibre apps.
   *
   * Fusion-render-mode only: mosaic mode has no per-station raster boundary
   * left to hit-test against (a mosaic pixel is just some station's byte,
   * unmarked — workers/CONTRACTS.md's "z7 paste alignment"), and no per-
   * station map markers exist to click on either, so there's nothing on the
   * map itself for this affordance to target there. The station strip
   * (buildStationStrip, unaffected by this gate) remains the click-through
   * path into single-station view in mosaic mode — unchanged, as intended. */
  private wireCompositeMapInteractions(): void {
    this.map.on("click", (e) => {
      if (this.viewMode !== "composite" || this.compositeRenderMode !== "fusion") return;
      const hit = this.hitTestStation(e.lngLat);
      if (hit) this.enterStationMode(hit);
    });
    this.map.on("mousemove", (e) => {
      if (this.viewMode !== "composite" || this.compositeRenderMode !== "fusion") return;
      this.map.getCanvas().style.cursor = this.hitTestStation(e.lngLat) ? "pointer" : "";
    });
  }

  /** Which station is under a click/hover point — delegates to
   * FusionController's own hit test (against real per-station bboxes read
   * off each COG, fusionController.ts's slotBbox) when the WebGL compositor
   * is doing the rendering; falls back to the legacy per-layer test below
   * only when it isn't (no single MapLibre layer per station exists once
   * fusion has drawn them all into one canvas, so MapLibre's own
   * queryRenderedFeatures delegation can't help either way — see the
   * legacy method's own doc comment). */
  private hitTestStation(lngLat: maplibregl.LngLat): string | null {
    if (this.fusion.fusionAvailable) return this.fusion.stationAtLngLat(lngLat.lng, lngLat.lat);
    return this.legacyStationAtLngLat(lngLat);
  }

  /** Topmost (last-painted, per STATIONS order) station whose current frame's
   * bounding box contains the point; null if none (COGs are north-up/unrotated —
   * geo.ts — so each frame's extent is an axis-aligned lon/lat box, no polygon
   * math needed). Hit-tests the frame's full raster extent, not per-pixel alpha —
   * good enough for v1, matches the layer-stacking overlap rule already in place.
   * Legacy fallback path only (see hitTestStation) — MapLibre's per-layer
   * `map.on('click'|'mouseenter', layerId, cb)` delegation is implemented via
   * queryRenderedFeatures, which does not support raster layers (verified
   * empirically: it returns zero features for an `image`-source raster layer
   * even dead-center on it), which is why this exists as a manual test at all. */
  private legacyStationAtLngLat(lngLat: maplibregl.LngLat): string | null {
    for (let i = STATIONS.length - 1; i >= 0; i--) {
      const id = STATIONS[i].id;
      const c = this.compositeFrameBounds.get(id);
      if (c && lngLat.lng >= c.topLeft[0] && lngLat.lng <= c.topRight[0] && lngLat.lat <= c.topLeft[1] && lngLat.lat >= c.bottomLeft[1]) {
        return id;
      }
    }
    return null;
  }

  private setCompositeLayersVisibility(visible: boolean): void {
    if (!this.mapReady) return;
    for (const s of STATIONS) {
      const layerId = compositeLayerId(s.id);
      if (this.map.getLayer(layerId)) this.map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
    }
  }

  /** Sets every legacy per-station composite layer's opacity at once — used
   * only in the WebGL-fusion-unavailable fallback (fusionController.ts's
   * `fusionAvailable`); there's no more zoom-driven wide-view handoff to
   * animate (workers/CONTRACTS.md 2026-08-10: fusion renders at every zoom
   * in the wide view — see fusionController.ts's own doc comment), so this
   * is a plain on/off now rather than something a zoom listener drives. */
  private setCompositeLayersOpacity(opacity: number): void {
    if (!this.mapReady) return;
    for (const s of STATIONS) {
      const layerId = compositeLayerId(s.id);
      if (this.map.getLayer(layerId)) this.map.setPaintProperty(layerId, "raster-opacity", opacity);
    }
  }

  /** Shows each station's latest known frame on the legacy layers — the
   * fusion-unavailable fallback's live-mode catch-up, and compositeGoLive()'s
   * own body when that's the active path (extracted so both share it
   * instead of duplicating the loop). No-op (harmlessly) when fusion IS
   * available, since nothing calls it in that case. */
  private refreshAllCompositeFrames(): void {
    for (const [id, manifest] of this.compositeManifests) {
      const latest = manifest?.latest ?? manifest?.frames.at(-1) ?? null;
      if (latest) void this.showCompositeFrame(id, latest.ts);
    }
  }

  private fitAllStationBounds(): void {
    if (!this.mapReady) return;
    const bounds = new maplibregl.LngLatBounds();
    // ~1.6deg margin approximates a NEXRAD surveillance cut's ~230km range ring
    // around each site, so the fitted view shows full footprints, not just the towers.
    const pad = 1.6;
    for (const s of STATIONS) {
      bounds.extend([s.lon - pad, s.lat - pad]);
      bounds.extend([s.lon + pad, s.lat + pad]);
    }
    this.map.fitBounds(bounds, { padding: 40, duration: 0 });
  }

  // ---- composite pollers (one per station, staggered) ----

  private ensureCompositePollers(): void {
    if (this.compositePollers.size > 0) return;
    for (const s of STATIONS) {
      const poller = new ManifestPoller(
        () => manifestUrl(getBaseUrl(), s.id),
        (m) => this.onCompositeManifest(s.id, m),
        (err) => this.onCompositeManifestError(s.id, err),
        COMPOSITE_POLL_INTERVAL_MS,
      );
      this.compositePollers.set(s.id, poller);
    }
  }

  private startCompositePollers(): void {
    this.ensureCompositePollers();
    this.stopCompositeStaggerTimers();
    const staggerStep = COMPOSITE_POLL_INTERVAL_MS / STATIONS.length;
    STATIONS.forEach((s, i) => {
      const timer = setTimeout(() => this.compositePollers.get(s.id)?.start(), i * staggerStep);
      this.compositeStaggerTimers.push(timer);
    });
  }

  private stopCompositePollers(): void {
    this.stopCompositeStaggerTimers();
    for (const poller of this.compositePollers.values()) poller.stop();
  }

  private stopCompositeStaggerTimers(): void {
    for (const t of this.compositeStaggerTimers) clearTimeout(t);
    this.compositeStaggerTimers = [];
  }

  private onCompositeManifest(id: string, manifest: HotManifest): void {
    this.compositeManifests.set(id, manifest);
    // Per-station manifests (small JSON) keep polling regardless of whether
    // fusion is doing the rendering — loop/scrub history and the fleet.json
    // fallback both need them either way (fusionController.ts's
    // onCompositeManifestsChanged). The frame IMAGE is only fetched+decoded
    // here in the legacy fallback path; when fusion is available it does its
    // own per-station COG decode (at the zoom-appropriate overview level)
    // straight from this same manifest data, so doing it twice here would be
    // pure waste.
    this.fusion.onCompositeManifestsChanged(this.compositeManifests);
    // fusion.initialized gates this, not just fusionAvailable: manifest polls
    // start synchronously in the constructor, well before the map's "load"
    // event runs fusion.init() — a fast (e.g. localhost) response can arrive
    // before fusionAvailable has been determined at all, and its pre-init
    // default (false) must never be misread as "confirmed unavailable, use
    // the legacy fetch" (verified empirically: this was a real bug — KAMX's
    // manifest routinely won that race, firing a spurious full-file fetch on
    // cold load even though fusion was about to be available all along).
    if (this.fusion.initialized && !this.fusion.fusionAvailable && this.compositeMode === "live") {
      const latest = manifest.latest ?? manifest.frames.at(-1) ?? null;
      if (latest && latest.ts !== this.compositeDisplayedTs.get(id)) {
        void this.showCompositeFrame(id, latest.ts);
      }
    }
    this.refreshCompositeScrubRange();
    this.renderStationStrip();
  }

  private onCompositeManifestError(id: string, _err: unknown): void {
    // A 404 means the station isn't baking (offline, or staged rollout hasn't reached
    // it yet) — this is a normal, expected state (e.g. KLIX in production), not an
    // error worth a status-line banner. Leave any previously-known manifest in place
    // so a transient blip doesn't blank an already-fresh station; the strip's dot
    // will age to red on its own if the station really has gone quiet. Only stations
    // we've NEVER heard from get an explicit null entry, so the strip shows them as
    // "missing" from the start rather than not appearing at all.
    if (!this.compositeManifests.has(id)) {
      this.compositeManifests.set(id, null);
      this.fusion.onCompositeManifestsChanged(this.compositeManifests);
    }
    this.renderStationStrip();
  }

  // ---- composite frame loading / display ----

  private async showCompositeFrame(id: string, ts: string): Promise<void> {
    const token = (this.compositeLoadTokens.get(id) ?? 0) + 1;
    this.compositeLoadTokens.set(id, token);
    const key = frameCacheKey(id, ts);

    let decoded = this.cache.get(key);
    if (!decoded) {
      try {
        decoded = await fetchAndDecodeFrame(frameUrl(getBaseUrl(), id, ts));
      } catch (err) {
        this.setCompositeStatus(`${id} frame ${ts}: ${(err as Error).message ?? err}`, true);
        return;
      }
      this.cache.set(key, decoded);
    }
    if (this.compositeLoadTokens.get(id) !== token) return; // superseded by a newer request for this station

    this.applyCompositeFrameToMap(id, decoded);
    this.compositeDisplayedTs.set(id, ts);
  }

  private applyCompositeFrameToMap(id: string, decoded: DecodedFrame): void {
    if (!this.mapReady) {
      this.map.once("load", () => this.applyCompositeFrameToMap(id, decoded));
      return;
    }
    const coordinates = cornersToMapLibreCoordinates(decoded.corners);
    const source = this.map.getSource(compositeSourceId(id)) as maplibregl.ImageSource | undefined;
    source?.updateImage({ url: decoded.url, coordinates });
    this.compositeFrameBounds.set(id, decoded.corners);
  }

  private clearCompositeFrame(id: string): void {
    if (this.compositeDisplayedTs.get(id) === null || !this.mapReady) return;
    this.compositeLoadTokens.set(id, (this.compositeLoadTokens.get(id) ?? 0) + 1); // drop any in-flight fetch for this station
    const s = stationById(id);
    const source = this.map.getSource(compositeSourceId(id)) as maplibregl.ImageSource | undefined;
    source?.updateImage({ url: TRANSPARENT_PIXEL, coordinates: placeholderCoordinates(s.lon, s.lat) });
    this.compositeDisplayedTs.set(id, null);
    this.compositeFrameBounds.set(id, null);
  }

  // ---- composite station strip + hero counter ----

  private buildStationStrip(): void {
    this.el.stationStrip.innerHTML = "";
    this.stripItemEls.clear();
    for (const s of STATIONS) {
      const item = document.createElement("div");
      item.className = "strip-item";
      item.setAttribute("role", "listitem");
      item.tabIndex = 0;
      item.setAttribute("aria-label", `${s.id} — view single-station`);

      const dot = document.createElement("span");
      dot.className = "strip-dot";
      dot.dataset.state = "stale";

      const idLabel = document.createElement("span");
      idLabel.className = "strip-id";
      idLabel.textContent = s.id;

      const age = document.createElement("span");
      age.className = "strip-age";
      age.textContent = "--:--";

      item.append(dot, idLabel, age);
      item.addEventListener("click", () => this.enterStationMode(s.id));
      item.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.enterStationMode(s.id);
        }
      });

      this.el.stationStrip.appendChild(item);
      this.stripItemEls.set(s.id, { dot, age });
    }
  }

  private renderStationStrip(): void {
    const nowMs = Date.now();
    const strip =
      this.compositeRenderMode === "mosaic"
        ? deriveMosaicStationStrip(this.mosaic.stationRoster(), this.mosaic.currentContributing(), this.mosaic.currentFrameBakeTimeMs(), nowMs)
        : deriveStationStrip(STATIONS.map((s) => s.id), this.compositeManifests, nowMs);
    for (const entry of strip) {
      const els = this.stripItemEls.get(entry.id);
      if (!els) continue;
      els.dot.dataset.state = entry.dotState;
      els.age.textContent = entry.ageSec === null ? "--:--" : formatMMSS(entry.ageSec);
    }
    this.updateCompositeHero(strip);

    // Only touch the status line for the initial "nothing has reported yet" window
    // (first poll cycle); per-station errors afterward are the strip's job.
    const WAITING_MSG = this.compositeRenderMode === "mosaic" ? "waiting for mosaic…" : "waiting for stations…";
    const nothingYet = this.compositeRenderMode === "mosaic" ? this.mosaic.manifest() === null : this.compositeManifests.size === 0;
    if (nothingYet) this.setCompositeStatus(WAITING_MSG);
    else if (this.el.cStatusLine.textContent === "waiting for mosaic…" || this.el.cStatusLine.textContent === "waiting for stations…") {
      this.setCompositeStatus("");
    }
  }

  /** Freshest-station hero counter — same StationStripEntry[] shape either
   * way (stationStrip.ts), just derived differently upstream depending on
   * compositeRenderMode (renderStationStrip, above): the mosaic path reads
   * one frame's contributing list, the fusion path reads N per-station
   * manifests. heroLabel()/the pipeline-latency source below are the only
   * bits that actually differ once `strip` is in hand. */
  private updateCompositeHero(strip: StationStripEntry[]): void {
    this.el.cAgeLabel.textContent = this.heroLabel();
    const freshest = pickFreshest(strip);
    if (!freshest || freshest.ageSec === null) {
      this.el.cAgeValue.textContent = "--:--";
      this.el.cAgePanel.dataset.state = "stale";
      this.el.cMetaStation.textContent = "--";
      this.el.cMetaPipeline.textContent = "pipeline --";
      return;
    }
    this.el.cAgeValue.textContent = formatMMSS(freshest.ageSec);
    // Same fresh/aging/stale thresholds as the strip dots (90s/240s) so the hero
    // panel never disagrees with that station's own dot.
    this.el.cAgePanel.dataset.state = freshest.dotState;
    this.el.cMetaStation.textContent = freshest.id;
    // Mosaic mode: the contributing list doesn't carry a per-station bake
    // latency, so the pipeline figure is the MOSAIC frame's own compose
    // latency instead — a more meaningful number than any one station's
    // anyway. Fusion mode: unchanged, the freshest station's own latency.
    const latencyMs = this.compositeRenderMode === "mosaic" ? this.mosaic.currentFramePipelineLatencyMs() : freshest.bakeLatencyMs;
    this.el.cMetaPipeline.textContent = latencyMs !== null ? `pipeline: ${formatLatencySec(latencyMs)}` : "pipeline --";
  }

  private heroLabel(): string {
    if (this.compositeRenderMode === "mosaic") return "freshest station · CONUS mosaic";
    return this.fusion.fusionAvailable ? `freshest station · fused (${this.fusion.getBlendMode()})` : "freshest station · radar data age";
  }

  // ---- composite transport (live / loop-scrub across all stations) ----

  /** WHILE dragging, "input" fires far more often than a full decode+
   * (field-smooth)+render pipeline can keep up with (that pipeline can run
   * anywhere from ~15ms to several hundred ms depending on LOD/sigma — see
   * mosaicGlFieldSmoothing.ts's PERFORMANCE doc comment) — queuing one
   * full pipeline run per tick is exactly the "never queue N decodes for N
   * slider ticks" failure mode. Instead: every tick gets an INSTANT,
   * cache-only repaint (showNearestLoaded() — never fetches/decodes/
   * blurs) so the display always has SOMETHING moving, and the actual
   * exact-target decode only fires once the drag goes idle for
   * SETTLE_IDLE_MS or the user releases (the "change" event) — not on
   * every tick in between. */
  private wireCompositeTransport(): void {
    this.el.cLiveBtn.addEventListener("click", () => this.compositeGoLive());
    this.el.cPlayBtn.addEventListener("click", () => this.compositeTogglePlay());

    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    const settleNow = (ms: number): void => {
      if (settleTimer !== undefined) {
        clearTimeout(settleTimer);
        settleTimer = undefined;
      }
      this.mosaic.setCrossfadeEnabled(true);
      this.compositeSeekTo(ms);
    };

    this.el.cScrubber.addEventListener("input", () => {
      this.compositeEnterLoopMode();
      this.stopCompositeLoopTimer();
      const ms = Number(this.el.cScrubber.value);

      this.el.cFrameTs.textContent = formatFrameTsFromMs(ms);
      // The crossfade transition (mosaicController.ts's CROSSFADE_MS) makes
      // these rapid instant-feedback repaints look smeared instead of
      // instant — suppressed for the duration of the drag, restored at
      // settle (settleNow, above).
      this.mosaic.setCrossfadeEnabled(false);
      this.mosaic.showNearestLoaded(ms);
      this.mosaicGl.showNearestLoaded(ms);

      if (settleTimer !== undefined) clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        settleTimer = undefined;
        settleNow(ms);
      }, SCRUB_SETTLE_IDLE_MS);
    });

    // Drag release (mouseup/touchend/keyboard commit) fires "change" once —
    // don't wait out the idle timer if the interaction has already ended.
    this.el.cScrubber.addEventListener("change", () => settleNow(Number(this.el.cScrubber.value)));
  }

  private refreshCompositeScrubRange(): void {
    const range = this.compositeRenderMode === "mosaic" ? this.mosaic.timeRange() : globalTimeRange(this.compositeManifests);
    if (!range) return;
    this.el.cScrubber.min = String(range.minMs);
    this.el.cScrubber.max = String(range.maxMs);
    if (this.compositeMode === "live") this.el.cScrubber.value = String(range.maxMs);
  }

  private compositeEnterLoopMode(): void {
    if (this.compositeMode === "loop") return;
    this.compositeMode = "loop";
    this.updateCompositeTransportUi();
  }

  private compositeGoLive(): void {
    this.compositeMode = "live";
    this.compositeLoopPlaying = false;
    this.stopCompositeLoopTimer();
    this.updateCompositeTransportUi();
    this.el.cFrameTs.textContent = "live";
    if (this.compositeRenderMode === "mosaic") {
      this.mosaic.goLive();
      this.mosaicGl.goLive();
      const range = this.mosaic.timeRange();
      if (range) this.el.cScrubber.value = String(range.maxMs);
      return;
    }
    this.fusion.goLive();
    const range = globalTimeRange(this.compositeManifests);
    if (range) this.el.cScrubber.value = String(range.maxMs);
    if (!this.fusion.fusionAvailable) this.refreshAllCompositeFrames();
  }

  private compositeTogglePlay(): void {
    this.compositeEnterLoopMode();
    this.compositeLoopPlaying = !this.compositeLoopPlaying;
    this.updateCompositeTransportUi();
    if (this.compositeLoopPlaying) this.startCompositeLoopTimer();
    else this.stopCompositeLoopTimer();
  }

  private startCompositeLoopTimer(): void {
    this.stopCompositeLoopTimer();
    this.compositeLoopTimer = setInterval(() => {
      const range = this.compositeRenderMode === "mosaic" ? this.mosaic.timeRange() : globalTimeRange(this.compositeManifests);
      if (!range) return;
      const step = (range.maxMs - range.minMs) / COMPOSITE_LOOP_STEPS;
      this.compositeScrubTimeMs = this.compositeScrubTimeMs < range.minMs ? range.minMs : this.compositeScrubTimeMs + step;
      if (this.compositeScrubTimeMs > range.maxMs) this.compositeScrubTimeMs = range.minMs;
      this.el.cScrubber.value = String(this.compositeScrubTimeMs);
      this.applyCompositeScrubFrame();
    }, LOOP_INTERVAL_MS);
  }

  private stopCompositeLoopTimer(): void {
    if (this.compositeLoopTimer !== undefined) clearInterval(this.compositeLoopTimer);
    this.compositeLoopTimer = undefined;
  }

  private stopCompositeLoop(): void {
    this.compositeLoopPlaying = false;
    this.stopCompositeLoopTimer();
    this.updateCompositeTransportUi();
  }

  private compositeSeekTo(ms: number): void {
    this.compositeScrubTimeMs = ms;
    this.applyCompositeScrubFrame();
  }

  private applyCompositeScrubFrame(): void {
    this.el.cFrameTs.textContent = formatFrameTsFromMs(this.compositeScrubTimeMs);
    if (this.compositeRenderMode === "mosaic") {
      this.mosaic.showAtOrBefore(this.compositeScrubTimeMs);
      this.mosaicGl.showAtOrBefore(this.compositeScrubTimeMs);
      return;
    }
    // Shared scrub axis drives fusion's own per-station decode too (goal:
    // one LIVE/Play/scrub transport for the whole wide view).
    this.fusion.showAtOrBefore(this.compositeScrubTimeMs);
    if (this.fusion.fusionAvailable) return; // fusion handles its own per-station decode; the legacy layers below aren't visible
    for (const [id, manifest] of this.compositeManifests) {
      if (!manifest) continue;
      const frame = frameAtOrBefore(manifest.frames, this.compositeScrubTimeMs);
      if (frame) void this.showCompositeFrame(id, frame.ts);
      else this.clearCompositeFrame(id); // no coverage at this scrub time -> station shows nothing
    }
  }

  private updateCompositeTransportUi(): void {
    this.el.cLiveBtn.setAttribute("aria-pressed", String(this.compositeMode === "live"));
    this.el.cPlayBtn.setAttribute("aria-pressed", String(this.compositeLoopPlaying));
    this.el.cPlayBtn.textContent = this.compositeLoopPlaying ? "⏸ Pause" : "▶ Play";
  }

  // ---- shared: ticking + status + URL sync ----

  private tick(): void {
    this.tickAgeCounter();
    this.renderStationStrip();
    this.updateLabStatus();
  }

  private setStatus(msg: string, isError = false): void {
    this.el.statusLine.textContent = msg;
    this.el.statusLine.dataset.error = String(isError);
  }

  private setCompositeStatus(msg: string, isError = false): void {
    this.el.cStatusLine.textContent = msg;
    this.el.cStatusLine.dataset.error = String(isError);
  }

  private syncUrl(): void {
    const url = new URL(location.href);
    if (this.viewMode === "station") url.searchParams.set("station", this.station);
    else url.searchParams.delete("station");
    history.replaceState(null, "", url);
  }
}

function formatFrameTs(ts: string): string {
  // "20260809T120102Z" -> "12:01:02Z"
  const m = /^\d{8}T(\d{2})(\d{2})(\d{2})Z$/.exec(ts);
  return m ? `${m[1]}:${m[2]}:${m[3]}Z` : ts;
}

function formatFrameTsFromMs(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}Z`;
}

function req<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el as T;
}
